/** Firebase Storage object path for Project Hub uploads: `projectFiles/{projectId}/{object}`. */
const PROJECT_FILES_PATH_RE = /^projectFiles\/([^/]+)\/(.+)$/;

/** Decode a download URL, encoded `/o/…` segment, or raw storage path into a Storage object path. */
export function normalizeStoragePath(raw: string): string {
  const s = (raw || '').trim();
  if (!s) return '';

  let path = s;
  const oMatch = s.match(/\/o\/([^?]+)/);
  if (oMatch) {
    try {
      path = decodeURIComponent(oMatch[1]);
    } catch {
      path = oMatch[1];
    }
  } else {
    try {
      const u = new URL(s);
      path = decodeURIComponent(u.pathname.replace(/^\//, ''));
    } catch {
      try {
        path = decodeURIComponent(s);
      } catch {
        path = s;
      }
    }
  }

  return path.split('?')[0].replace(/^\/+/, '');
}

/**
 * Last path segment, without the upload timestamp, the comment-attachment
 * random id, or the `~` delimiter we use to keep the original basename.
 *
 * `1730-bqwgn0t5l.png` stays `bqwgn0t5l.png` (the original name was never stored).
 * `1730-bqwgn0t5l~Church_notes.pdf` and `1730-ab12cd-Church_notes.pdf` both
 * become `Church_notes.pdf`.
 */
export function displayNameFromStorageObject(objectName: string): string {
  const base = (objectName || '').split('/').filter(Boolean).pop()?.split('?')[0] || '';
  if (!base) return 'File';
  const tilde = base.lastIndexOf('~');
  if (tilde >= 0 && tilde < base.length - 1) {
    const human = base.slice(tilde + 1).trim();
    if (human) return human;
  }
  const noTs = base.replace(/^\d{10,}-/, '');
  const tagged = noTs.match(/^([a-z0-9]{6,12})-(.+)$/i);
  if (tagged && /\d/.test(tagged[1]) && tagged[2].includes('.')) return tagged[2];
  return noTs || base;
}

/** True when a label is only the generated storage id, e.g. `bqwgn0t5l.png`. */
export function isGeneratedStorageLabel(name: string): boolean {
  const stem = name.split('.')[0] || '';
  return /^[a-z0-9]{6,12}\.[a-z0-9]{2,8}$/i.test(name) && /\d/.test(stem) && !name.includes('_');
}

export function parseProjectFilesStoragePath(
  raw: string,
): { projectId: string; objectName: string; fileName: string } | null {
  const path = normalizeStoragePath(raw);
  const m = path.match(PROJECT_FILES_PATH_RE);
  if (!m) return null;
  const objectName = m[2];
  const fileName = displayNameFromStorageObject(objectName);
  return { projectId: m[1], objectName, fileName };
}

/** Human filename for viewer headers — never the raw `projectFiles/…` path. */
export function displayNameFromFileRef(raw: string): string {
  const path = normalizeStoragePath(raw);
  const last = path.split('/').filter(Boolean).pop() || raw.trim();
  return displayNameFromStorageObject(last);
}

function titleLooksLikeStorageRef(raw: string): boolean {
  return (
    !!parseProjectFilesStoragePath(raw)
    || raw.includes('projectFiles/')
    || raw.includes('task-comments/')
    || raw.includes('~')
    || /^https?:\/\//i.test(raw)
  );
}

/**
 * Safe header title for PDF/image viewers. Never throws on null/undefined title
 * (a render crash here blanks the whole CRM shell — CRM feedback #39).
 *
 * Pass the file URL when the title may be only the generated storage id.
 */
export function displayTitleForViewer(titleProp: string | null | undefined, url?: string | null): string {
  const raw = typeof titleProp === 'string' ? titleProp.trim() : '';
  const fromUrl = url ? displayNameFromFileRef(url) : '';
  if (!raw) return fromUrl || 'Document';
  const fromTitle = titleLooksLikeStorageRef(raw) ? displayNameFromFileRef(raw) : raw;
  if (isGeneratedStorageLabel(fromTitle) && fromUrl && !isGeneratedStorageLabel(fromUrl)) return fromUrl;
  return fromTitle || 'Document';
}
